import { Form } from 'antd';
import React, { ReactNode, useCallback } from 'react';
import { AttachmentButton, FilesCard } from '@common/components';
import { useMutation, useNavigate } from '@common/hooks';
import { buildDialogUrl } from '@common/services';
import {
  DeleteDetailItemAttachmentMutation,
  DetailItemAttachmentType,
} from '@onboarding/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ComplianceItemFilesListCustomHeader } from './_compliance-item-files-list-custom-header';

type Props = {
  attachmentList?: DetailItemAttachmentType[];
  onDeleted?: () => void;
  complianceItemId: string;
  complianceItemStateApprovedAt: string | null;
  refetchQueries: string[];
  formContent?: ReactNode;
};

export function ComplianceItemFilesListV2({
  attachmentList = [],
  onDeleted,
  complianceItemId,
  complianceItemStateApprovedAt,
  refetchQueries,
  formContent,
}: Props) {
  const navigate = useNavigate();

  const filesExist = attachmentList.length >= 1;

  const [deleteFile, { loading: deleteLoading }] = useMutation(
    DeleteDetailItemAttachmentMutation,
    {
      refetchQueries,
      onCompleted: () => {
        createMessage('success', 'File has been successfully deleted.');
        onDeleted?.();
      },
      onError: (error) => {
        console.error('DeleteDetailItemAttachmentMutation error:', error);
        createMessage('error', 'Failed to delete file. Please try again.');
      },
    }
  );

  const onDeleteFile = useCallback(
    (attachmentId: string) => {
      if (deleteLoading) {
        return;
      }

      deleteFile({
        variables: { detailItemAttachmentId: attachmentId },
      });
    },
    [deleteFile, deleteLoading]
  );

  const openAddAttachmentDrawer = () => {
    const updateDrawerUrl = buildDialogUrl(
      'compliance-item-add-attachment-drawer',
      {
        props: { complianceItemId },
      }
    );

    navigate(updateDrawerUrl);
  };

  return (
    filesExist && (
      <>
        <Form.Item>
          <FilesCard
            hideTimezone
            customHeader={
              <ComplianceItemFilesListCustomHeader
                complianceItemStateApprovedAt={complianceItemStateApprovedAt}
                formContent={formContent}
              />
            }
            attachedFiles={attachmentList}
            deleteLoading={deleteLoading}
            onDelete={onDeleteFile}
          />
        </Form.Item>

        <AttachmentButton onClick={openAddAttachmentDrawer}>
          Add File
        </AttachmentButton>
      </>
    )
  );
}
